// ============================================================
// Typed API client. Every network call in the app goes through the
// `api` object below — pages and components never call fetch directly.
//
// Static Web Apps carries the session cookie for us, so there is no
// token handling here at all. That is the whole point of the pivot off
// MSAL-in-the-browser: the SPA holds no credentials, and the API
// trusts only the x-ms-client-principal header SWA injects.
// ============================================================

import type {
  AuditEntry,
  CurrentUser,
  DocumentListing,
  EquipmentInput,
  NewReservation,
  Reservation,
  ReservationEdit,
  Resource,
  UploadGrant,
} from './types';

/** Thrown when the API says the caller has no session. */
export class NotAuthenticatedError extends Error {
  constructor() {
    super('not signed in');
    this.name = 'NotAuthenticatedError';
  }
}

/** Thrown for any other non-2xx response, carrying the server's message. */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Field names out of a zod `.format()` tree, which the API sends as
 * `details` on a 400.
 *
 * Without this the client threw the message away and showed only
 * "invalid request body". That cost real time: 39 catalogue rows held a
 * calibration status outside the API's vocabulary, and every attempt to
 * edit one failed on a field the admin had never touched, with nothing
 * naming it. The server was sending the answer all along.
 *
 * Shape is `{ _errors: [], field: { _errors: ['...'] } }`, nested for
 * nested objects. Collect the dotted path of every node that has
 * complaints of its own.
 */
function invalidFields(details: unknown, prefix = ''): string[] {
  if (!details || typeof details !== 'object') return [];
  const found: string[] = [];
  for (const [key, value] of Object.entries(details as Record<string, unknown>)) {
    if (key === '_errors') {
      if (Array.isArray(value) && value.length > 0 && prefix) found.push(prefix);
      continue;
    }
    found.push(...invalidFields(value, prefix ? `${prefix}.${key}` : key));
  }
  return found;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (response.status === 401) throw new NotAuthenticatedError();

  if (!response.ok) {
    // The API always answers JSON, but a platform-level failure (a cold
    // Function host, a gateway timeout) can return HTML. Fall back to
    // the status text rather than blowing up on a parse error.
    let message = `request failed (${response.status})`;
    let code: string | undefined;
    try {
      const body = await response.json();
      if (body?.error) message = String(body.error);
      if (body?.code) code = String(body.code);
      // Name the fields the server objected to. A bare "invalid request
      // body" gives the user nothing to act on, least of all when the
      // culprit is a stored value they never edited.
      const fields = invalidFields(body?.details);
      if (fields.length > 0) message += `: ${fields.join(', ')}`;
      if (body?.errorId) message += ` [${body.errorId}]`;
    } catch {
      /* keep the generic message */
    }
    throw new ApiError(response.status, message, code);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  me: () => request<CurrentUser>('/me'),

  /** `includeAll` drops the active + Available filters (catalogue view). */
  listResources: (includeAll = false) =>
    request<Resource[]>(`/equipment${includeAll ? '?all=1' : ''}`),

  createEquipment: (input: Omit<EquipmentInput, 'isActive'>) =>
    request<Resource>('/equipment', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  updateEquipment: (id: number, input: Partial<EquipmentInput>) =>
    request<Resource>(`/equipment/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  /** Files stored against one resource. 501 when S3 isn't configured. */
  listDocuments: (equipmentId: number) =>
    request<DocumentListing>(`/equipment/${equipmentId}/documents`),

  /**
   * A short-lived download URL for one file. Returned as JSON rather
   * than a redirect so the signed URL stays out of browser history.
   */
  documentUrl: (equipmentId: number, key: string) =>
    request<{ url: string }>(
      `/equipment/${equipmentId}/documents/download?key=${encodeURIComponent(key)}`,
    ),

  /**
   * Ask for permission to add a file. The API picks the key; the
   * browser then POSTs the file itself to S3 with the returned fields.
   */
  requestUpload: (equipmentId: number, fileName: string, contentType: string) =>
    request<UploadGrant>(`/equipment/${equipmentId}/documents/upload`, {
      method: 'POST',
      body: JSON.stringify({ fileName, contentType }),
    }),

  listReservations: () => request<Reservation[]>('/reservations'),

  /** The change trail. Admin only — 403 for everyone else. */
  listAudit: (limit = 200) => request<AuditEntry[]>(`/audit?limit=${limit}`),

  createReservation: (input: NewReservation) =>
    request<Reservation>('/reservations', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** Set the booking's resource, or pass '' to unassign it. */
  assignReservation: (id: number, equipment: string) =>
    request<Reservation>(`/reservations/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({ equipment }),
    }),

  /**
   * Edit a booking in place, keeping its reference and history. Send
   * only the changed fields. The resource goes through
   * `assignReservation`, cancelling through `cancelReservation`.
   */
  updateReservation: (id: number, changes: Partial<ReservationEdit>) =>
    request<Reservation>(`/reservations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(changes),
    }),

  cancelReservation: (
    id: number,
    canceledBy: string,
    cancellationReason: string,
  ) =>
    request<Reservation>(`/reservations/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ canceledBy, cancellationReason }),
    }),
};

/** Full-page navigation to the SWA sign-in endpoint. */
export function login(): void {
  const target = encodeURIComponent(
    window.location.pathname + window.location.search,
  );
  window.location.href = `/.auth/login/aad?post_login_redirect_uri=${target}`;
}

/** Full-page navigation to the SWA sign-out endpoint. */
export function logout(): void {
  window.location.href = '/.auth/logout?post_logout_redirect_uri=/';
}
